import { useEffect } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { useQueryClient } from '@tanstack/react-query';
import type { Signal } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import {
  SIGNAL_ASSIGNED_SSE_EVENT,
  SIGNAL_UNASSIGNED_SSE_EVENT,
  type SignalAssignmentEventPayload,
} from '@terra/shared-contracts-signals';

interface SignalsQueryData {
  signals: Signal[];
}

type ExecutionLike = {
  signalId?: string;
  signal?: {
    assigneeId?: string | null;
  };
  [key: string]: unknown;
};

const ExecutionLikeSchema = z.looseObject({
  signalId: z.string().optional(),
  signal: z
    .object({
      assigneeId: z.string().nullable().optional(),
    })
    .optional(),
});

function isExecutionLike(value: unknown): value is ExecutionLike {
  return ExecutionLikeSchema.safeParse(value).success;
}

export const useSignalAssignmentSSE = ({
  applicationId,
}: {
  applicationId?: string;
}) => {
  const { subscribe } = useSSE();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!applicationId) return;

    const handleAssignment = ({
      signalId,
      targetId: eventTargetId,
      assigneeId,
    }: SignalAssignmentEventPayload) => {
      if (eventTargetId !== applicationId) {
        return;
      }

      // Update signals query cache
      queryClient.setQueriesData(
        { queryKey: ['endpoint-signals-paginated'], exact: false },
        (oldData: SignalsQueryData | undefined) => {
          if (!oldData || !oldData.signals) return oldData;

          const updatedSignals = oldData.signals.map((signal) => {
            if (signal.id === signalId) {
              return {
                ...signal,
                assigneeId: assigneeId || null,
              };
            }
            return signal;
          });

          return {
            ...oldData,
            signals: updatedSignals,
          };
        },
      );

      // Update executions cache to reflect signal assignee changes
      queryClient.setQueriesData(
        { queryKey: ['executions'], exact: false },
        (
          oldData: { executions: unknown[]; totalCount: number } | undefined,
        ) => {
          if (!oldData || !oldData.executions) return oldData;

          const updatedExecutions = oldData.executions.map(
            (execution): unknown => {
              if (isExecutionLike(execution)) {
                if (execution.signalId === signalId && execution.signal) {
                  return {
                    ...execution,
                    signal: {
                      ...execution.signal,
                      assigneeId: assigneeId || null,
                    },
                  };
                }
              }

              return execution;
            },
          );

          return {
            ...oldData,
            executions: updatedExecutions,
          };
        },
      );
    };

    const unsubscribeAssigned = subscribe(
      SIGNAL_ASSIGNED_SSE_EVENT,
      handleAssignment,
    );
    const unsubscribeUnassigned = subscribe(
      SIGNAL_UNASSIGNED_SSE_EVENT,
      handleAssignment,
    );

    return () => {
      unsubscribeAssigned();
      unsubscribeUnassigned();
    };
  }, [applicationId, subscribe, queryClient]);
};
